<template>
    <v-card>
        <v-card-title>{{ dashboardWelcomeTitle }}</v-card-title>
    </v-card>

    <v-container fluid>
        <dashboard-stats-row>
            <template #icon>
                <img :src="truckPng" alt="Truck" width="100" />
            </template>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.totalTrucks')"
                    :value="truckCount"
                    :color="'green'"
                    @click="$router.push('/dashboard/search-trucks')"
                >
                    <template #subtitle>
                        <v-icon size="18" class="mr-1">mdi-eye</v-icon>
                        {{ truckViews }} {{ $t("dashboard.views") }}
                    </template>
                </dashboard-summary-card>
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-reservation-card
                    :title="$t('dashboard.totalTruckReservations')"
                    :received="truckReceived"
                    :contracted="truckContracted"
                    entry-type="truck"
                />
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.expires6hours')"
                    :value="truckExpiring6h"
                    color="red"
                    @click="$router.push({ path: '/dashboard/search-trucks', query: { expiresIn: '6h' } })"
                >
                    <template #subtitle>{{ $t("dashboard.entries") }}</template>
                </dashboard-summary-card>
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.expires1day')"
                    :value="truckExpiring1d"
                    color="red"
                    @click="$router.push({ path: '/dashboard/search-trucks', query: { expiresIn: '1d' } })"
                >
                    <template #subtitle>{{ $t("dashboard.entries") }}</template>
                </dashboard-summary-card>
            </v-col>
        </dashboard-stats-row>

        <dashboard-stats-row>
            <template #icon>
                <img :src="warePng" alt="Ware" width="100" />
            </template>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.totalWares')"
                    :value="wareCount"
                    :color="'green'"
                    @click="$router.push('/dashboard/search-wares')"
                >
                    <template #subtitle>
                        <v-icon size="18" class="mr-1">mdi-eye</v-icon>
                        {{ wareViews }} {{ $t("dashboard.views") }}
                    </template>
                </dashboard-summary-card>
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-reservation-card
                    :title="$t('dashboard.totalWareReservations')"
                    :received="wareReceived"
                    :contracted="wareContracted"
                    entry-type="ware"
                />
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.expires4hours')"
                    :value="wareExpiring4h"
                    color="red"
                    @click="$router.push({ path: '/dashboard/search-wares', query: { expiresIn: '4h' } })"
                >
                    <template #subtitle>{{ $t("dashboard.entries") }}</template>
                </dashboard-summary-card>
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.expires1day')"
                    :value="wareExpiring1d"
                    color="red"
                    @click="$router.push({ path: '/dashboard/search-wares', query: { expiresIn: '1d' } })"
                >
                    <template #subtitle>{{ $t("dashboard.entries") }}</template>
                </dashboard-summary-card>
            </v-col>
        </dashboard-stats-row>

        <v-row>
            <v-col cols="12" sm="6" md="4" lg="3">
                <v-btn block @click="$router.push('/dashboard/publish-truck')">+ {{ $t("sidebar.publishTruck") }}</v-btn>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
                <v-btn block @click="$router.push('/dashboard/publish-ware')">+ {{ $t("sidebar.publishWare") }}</v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from "axios";
import { showAlertModal, entryExpiresWithin } from "@/utils";
import truckPng from "@/assets/Truck.png";
import warePng from "@/assets/Ware.png";
import DashboardStatsRow from "@/components/dashboard/DashboardStatsRow.vue";
import DashboardSummaryCard from "@/components/dashboard/DashboardSummaryCard.vue";
import DashboardReservationCard from "@/components/dashboard/DashboardReservationCard.vue";

export default {
    name: "HomeView",
    components: { DashboardStatsRow, DashboardSummaryCard, DashboardReservationCard },
    data() {
        return {
            truckPng,
            warePng,
            truckCount: 0,
            truckViews: 0,
            truckExpiring6h: 0,
            truckExpiring1d: 0,
            wareCount: 0,
            wareViews: 0,
            wareExpiring4h: 0,
            wareExpiring1d: 0,
            truckReceived: 0,
            truckContracted: 0,
            wareReceived: 0,
            wareContracted: 0,
        };
    },
    mounted() {
        this.loadDashboard();
    },
    computed: {
        dashboardWelcomeTitle() {
            return this.buildDashboardWelcomeTitle();
        },
    },
    methods: {
        showAlertModal,
        /** Card title from session user (job codes resolved like AddEmployee). */
        buildDashboardWelcomeTitle() {
            let user;
            try {
                user = JSON.parse(sessionStorage.getItem("user") || "{}");
            } catch {
                return this.$t("welcome");
            }
            const fullName = [user.first_name, user.last_name]
                .map((part) => (part == null ? "" : String(part).trim()))
                .filter(Boolean)
                .join(" ");
            const jobCode = user.job_title != null ? String(user.job_title) : "";
            const jobTitleKey = jobCode ? `jobTitle.${jobCode}` : "";
            const jobLabel =
                jobCode && jobTitleKey && this.$te(jobTitleKey)
                    ? this.$t(jobTitleKey)
                    : jobCode;
            const companyName =
                user.company_name != null ? String(user.company_name) : "";
            const details = [jobLabel, companyName].filter(Boolean).join(", ");
            if (!fullName && !details) return this.$t("welcome");
            if (fullName && details) {
                return this.$t("dashboard.welcomeWithDetails", {
                    name: fullName,
                    details,
                });
            }
            if (fullName) return this.$t("dashboard.welcomeWithName", { name: fullName });
            return this.$t("dashboard.welcomeDetailsOnly", { details });
        },
        async loadDashboard() {
            await Promise.all([this.fetchTruckAndWareCounts(), this.fetchReservationCounts()]);
        },
        async fetchTruckAndWareCounts() {
            try {
                const [truckResponse, wareResponse] = await Promise.all([
                    axios.get(`${process.env.VUE_APP_API_URL}/truck-requests/`),
                    axios.get(`${process.env.VUE_APP_API_URL}/ware-requests/`),
                ]);

                const trucks = truckResponse.data;
                const wares = wareResponse.data;

                this.truckCount = trucks.length;
                this.wareCount = wares.length;

                this.truckViews = trucks.reduce(
                    (viewSum, truck) => viewSum + (truck.view_count || 0),
                    0
                );
                this.wareViews = wares.reduce(
                    (viewSum, ware) => viewSum + (ware.view_count || 0),
                    0
                );

                this.truckExpiring6h = trucks.filter((truck) =>
                    entryExpiresWithin(truck, 6)
                ).length;
                this.truckExpiring1d = trucks.filter((truck) =>
                    entryExpiresWithin(truck, 24)
                ).length;

                this.wareExpiring4h = wares.filter(
                    (ware) =>
                        !ware.has_pending_reservation && entryExpiresWithin(ware, 4)
                ).length;
                this.wareExpiring1d = wares.filter(
                    (ware) =>
                        !ware.has_pending_reservation && entryExpiresWithin(ware, 24)
                ).length;
            } catch (error) {
                console.error("Failed to fetch truck/ware counts:", error);
                this.showAlertModal(this.$store, "Failed to fetch truck/ware counts", "danger", 12000, error);
            }
        },
        async fetchReservationCounts() {
            try {
                const reservationsApiBase = `${process.env.VUE_APP_API_URL}/reservations/`;
                const countsQuery = "dashboard_counts=true";
                const [contractedTrucksResponse, receivedTrucksResponse, contractedWaresResponse, receivedWaresResponse] =
                    await Promise.all([
                        axios.get(
                            `${reservationsApiBase}?my=contracted&entry_type=truck&${countsQuery}`
                        ),
                        axios.get(
                            `${reservationsApiBase}?my=received&entry_type=truck&${countsQuery}`
                        ),
                        axios.get(
                            `${reservationsApiBase}?my=contracted&entry_type=ware&${countsQuery}`
                        ),
                        axios.get(
                            `${reservationsApiBase}?my=received&entry_type=ware&${countsQuery}`
                        ),
                    ]);
                this.truckContracted = contractedTrucksResponse.data.length;
                this.truckReceived = receivedTrucksResponse.data.length;
                this.wareContracted = contractedWaresResponse.data.length;
                this.wareReceived = receivedWaresResponse.data.length;
            } catch (error) {
                console.error("Failed to fetch reservation counts:", error);
                this.showAlertModal(this.$store, "Failed to fetch reservation counts", "danger", 8000, error);
            }
        },
    },
};
</script>
